import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import './MFASetupModal.css';

interface MFASetupModalProps {
  secretCode: string;
  qrCodeUrl: string;
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
}

export const MFASetupModal: React.FC<MFASetupModalProps> = ({
  secretCode,
  qrCodeUrl,
  onVerify,
  onCancel,
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Generate QR code image
    QRCode.toDataURL(qrCodeUrl, { width: 256 })
      .then(setQrCodeDataUrl)
      .catch((err: Error) => console.error('QR code generation error:', err));
  }, [qrCodeUrl]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    try {
      await onVerify(verificationCode);
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="mfa-modal-overlay">
      <div className="mfa-modal">
        <h2>Set Up Two-Factor Authentication</h2>
        <p className="mfa-description">
          Scan this QR code with Google Authenticator or any TOTP-compatible app
        </p>

        <div className="qr-code-container">
          {qrCodeDataUrl ? (
            <img src={qrCodeDataUrl} alt="MFA QR Code" className="qr-code" />
          ) : (
            <div className="qr-code-loading">Generating QR code...</div>
          )}
        </div>

        <div className="secret-code-container">
          <p className="secret-code-label">Or enter this code manually:</p>
          <code className="secret-code">{secretCode}</code>
        </div>

        <form onSubmit={handleVerify} className="mfa-verify-form">
          <div className="form-group">
            <label htmlFor="verification-code">Enter 6-digit code from your app</label>
            <input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              disabled={isVerifying}
              autoComplete="off"
              className="verification-input"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="mfa-modal-actions">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
              disabled={isVerifying}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="verify-button"
              disabled={isVerifying || verificationCode.length !== 6}
            >
              {isVerifying ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </form>

        <div className="mfa-help">
          <p>
            <strong>Don't have Google Authenticator?</strong>
          </p>
          <p>
            Download it from the{' '}
            <a
              href="https://apps.apple.com/app/google-authenticator/id388497605"
              target="_blank"
              rel="noopener noreferrer"
            >
              App Store
            </a>{' '}
            or{' '}
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Play
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
